import type { EncodedResponse } from '../../data/types';
import type { ParticleLayoutName } from '../../motion/particles/types';
import type { DashboardTab, FilterState } from '../../state/filters';

export type StoryBeat = 0 | 1 | 2;
export type StoryChapterNumber = 1 | 2 | 3 | 4 | 5 | 6;

export interface StoryLocation {
  chapter: StoryChapterNumber;
  beat: StoryBeat;
}

export interface DashboardHandoffDescriptor {
  tab: DashboardTab;
  filters?: FilterState;
  label: string;
}

export interface StoryBeatDefinition {
  kind: 'question' | 'evidence' | 'conclusion';
  label: string;
  prompt: string;
  dots: string;
  change: string;
  layout: ParticleLayoutName;
  bandField?: keyof EncodedResponse;
  handoff: DashboardHandoffDescriptor;
}

export interface StoryChapterDefinition {
  number: StoryChapterNumber;
  id: `chapter-${StoryChapterNumber}`;
  marker: string;
  title: string;
  claim: string;
  summary: string;
  beats: readonly [StoryBeatDefinition, StoryBeatDefinition, StoryBeatDefinition];
}

const DASHBOARD = (tab: DashboardTab, label = 'この結果を詳しく見る'): DashboardHandoffDescriptor => ({ tab, label });

export const STORY_CHAPTERS: readonly StoryChapterDefinition[] = [
  {
    number: 1, id: 'chapter-1', marker: 'AUDIENCE', title: '来場者の中心',
    claim: '来場者の中心は、家族 × イベント × ライト層である。',
    summary: '1,386人を、誰と来たか・何を目的にしたか・来場頻度の順に読み解く。',
    beats: [
      { kind: 'question', label: '問い', prompt: 'ATCには、どんな人が集まっているのか。', dots: 'すべての点はアンケートに答えた1人を表します。', change: 'まずは全体像をそのまま見る。', layout: 'full-population', handoff: DASHBOARD('audience') },
      { kind: 'evidence', label: '根拠', prompt: '家族・イベント・ライト層が、同じ来場の中心をつくる。', dots: '点を同行者と主目的の帯へ並べ替えます。点は消えず、位置だけが変わります。', change: '家族929人、イベント913人、初回＋年1回854人が中心に現れる。', layout: 'companion', handoff: DASHBOARD('visit') },
      { kind: 'conclusion', label: '結論', prompt: '来場の入口から満足までを、一本の流れとして読む。', dots: '帯は認知経路・目的・満足度の関係を示します。認知経路は複数回答のため按分です。', change: 'イベントが入口になり、満足へつながっている。', layout: 'purpose', handoff: DASHBOARD('awareness') },
    ],
  },
  {
    number: 2, id: 'chapter-2', marker: 'SATISFACTION', title: '当日満足は高い',
    claim: '来てもらえれば、88.4%が満足して帰る。',
    summary: '高い評価を確認したあと、その評価がどこからの来場者に見られるかを地図で確かめる。',
    beats: [
      { kind: 'question', label: '問い', prompt: '当日の体験は、期待に応えられているか。', dots: '点を満足度の帯へ並べます。濃い帯ほど回答が多い状態です。', change: '満足・非常に満足が大半を占める。', layout: 'satisfaction', handoff: DASHBOARD('satisfaction') },
      { kind: 'evidence', label: '根拠', prompt: '満足した人は、どこから来ているのか。', dots: '郵便番号上3桁を使える回答だけを、都道府県の位置へ移します。', change: '全国の足跡を都道府県単位で可視化する。', layout: 'geographic', handoff: DASHBOARD('audience') },
      { kind: 'conclusion', label: '結論', prompt: '近隣と域外で、見逃せない差はあるか。', dots: '関西・大阪へ拡大し、地図に置けない点は外側のリングに残ります。', change: '559は3.3%、域外は0.6%が「あまり満足していない」。', layout: 'geographic', handoff: DASHBOARD('satisfaction') },
    ],
  },
  {
    number: 3, id: 'chapter-3', marker: 'AWARENESS', title: '認知の弱さ',
    claim: '高い満足は、ATC全体の理解を意味しない。',
    summary: '当日の満足と、館内にある機能の理解は別の指標として読む。',
    beats: [
      { kind: 'question', label: '問い', prompt: 'ATCに来る前に、オフィス機能は知られているか。', dots: '点を「初めて知った」とそれ以外へ分けます。', change: '736人、53.1%が来場後に初めて知った。', layout: 'office-awareness', handoff: DASHBOARD('awareness') },
      { kind: 'evidence', label: '根拠', prompt: '主要施設は、どこまで認識されているか。', dots: '点は施設認知の有無で静かに帯へ移ります。', change: '主要施設を一つも知らない回答は876人、63.2%。', layout: 'bands', bandField: 'facility_awareness', handoff: DASHBOARD('awareness') },
      { kind: 'conclusion', label: '結論', prompt: '満足と理解を、同じ意味にしてはいけない。', dots: '満足度の高さを保ったまま、未認知の大きさを重ねます。', change: '「楽しかった」と「ATCを理解した」は別の結果である。', layout: 'office-awareness', handoff: DASHBOARD('awareness') },
    ],
  },
  {
    number: 4, id: 'chapter-4', marker: 'NEEDS', title: '求められているもの',
    claim: '求められているのは、派手さより基礎機能である。',
    summary: '声の大きい要望ではなく、回答数の多い基礎的なニーズから差を読む。',
    beats: [
      { kind: 'question', label: '問い', prompt: '次の来場を支えるのは、どんな機能か。', dots: '全員を保ったまま、要望の順位を読みます。', change: '飲食725、家族533、子ども489、買い物400、休憩342。', layout: 'field', handoff: DASHBOARD('needs') },
      { kind: 'evidence', label: '根拠', prompt: '既存の専門資産は、どのニーズに接続できるか。', dots: '点の位置は変えず、可能な機能接続だけを線で示します。', change: '体験・学習、仕事・勉強には既存資産との接点がある。', layout: 'grid', handoff: DASHBOARD('needs') },
      { kind: 'conclusion', label: '結論', prompt: '大きな基礎ニーズは、なお未接続のままである。', dots: '未接続の点を残し、熱さの声20件を注記します。', change: '線は来場者の移動ではなく、機能上の可能性を示す。', layout: 'field', handoff: DASHBOARD('needs') },
    ],
  },
  {
    number: 5, id: 'chapter-5', marker: 'MEMORIES', title: 'エピソード',
    claim: 'ATCらしさは、来場者の記憶の中にある。',
    summary: '数字の外側にある、継続して語られる来場の記憶を読む。',
    beats: [
      { kind: 'question', label: '問い', prompt: 'ATCは、来場者の中で何として記憶されているか。', dots: '点は母集団のまま背景に残ります。声は回答を象徴する一部です。', change: '111件の回答機会から、93件の有効エピソードを確認した。', layout: 'field', handoff: DASHBOARD('needs') },
      { kind: 'evidence', label: '根拠', prompt: 'ひとつの旅の記憶から、場所の使われ方を読む。', dots: '動きは止め、文章を途中で遮らずに読める状態にします。', change: 'フェリーとバイクの旅が、ATCを目的地として語っている。', layout: 'field', handoff: DASHBOARD('needs') },
      { kind: 'conclusion', label: '結論', prompt: '記憶は、一人ひとりの生活・家族・イベントと結びつく。', dots: '背景の全員を残しながら、18本の監査済みヒーローへ進めます。', change: '個別の声を、全1,633断片のコレクションへつなぐ。', layout: 'field', handoff: { tab: 'audience', label: 'Voicesコレクションを見る' } },
    ],
  },
  {
    number: 6, id: 'chapter-6', marker: 'CONNECTION', title: '接続',
    claim: '満足で終わらせない。',
    summary: 'イベントで動いた人が、館内を知り、次の理由を持ち帰る流れとして結ぶ。',
    beats: [
      { kind: 'question', label: '問い', prompt: '高い満足を、次の来館理由へどうつなげるか。', dots: 'すべての点がふたたび同じ人口へ戻ります。', change: '一日の体験を、次回の選択までの流れで考える。', layout: 'full-population', handoff: DASHBOARD('audience') },
      { kind: 'evidence', label: '根拠', prompt: '来館・館内回遊・次の来館理由は、ひと続きである。', dots: '点は一つの人口のまま、3つの接点の背景をつくります。', change: 'イベントで人を動かし、館内でATC全体を見せる。', layout: 'full-population', handoff: DASHBOARD('visit') },
      { kind: 'conclusion', label: '結論', prompt: 'ATCに来る理由が、一日で閉じない設計へ。', dots: '1,386人全員を最後の母集団として残します。', change: '作成：経営戦略室 ATC', layout: 'full-population', handoff: DASHBOARD('audience', 'データを探索する') },
    ],
  },
] as const;

export function parseStoryHash(hash: string): StoryChapterNumber | null {
  const match = /^#chapter-([1-6])$/.exec(hash);
  return match ? Number(match[1]) as StoryChapterNumber : null;
}

/** A non-story hash is still a valid report deep link. It must never reopen onboarding. */
export function isReportDestinationHash(hash: string): boolean {
  return parseStoryHash(hash) !== null || hash === '#dashboard' || hash === '#voices' || hash === '#methodology';
}
